import React, { useEffect, createRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Platform,
  StyleSheet,
  View,
  KeyboardAvoidingView,
  ScrollView,
  StatusBar,
} from "react-native";
import { addReservations, addUser, signOutUser } from "../../redux/actions/actionCreators";
import { Text, Spacer, InfoBox, InputAccessoryNavigation } from "../../components";
import { Stack } from "../../components/layout/stack";
import { translate } from "../../i18n/translate";
import { useTheme } from "react-native-paper";
import { useIsMounted } from "../../hooks/useIsMounted";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { Auth } from "aws-amplify";
import { loginSchema } from "../home/schemas";
import { UserFields, CognitoErrors, InfoBoxType, InfoBoxIconType } from "../../enums";
import { ThemeType } from "../../theme/theme";
import { useFocusEffect, useNavigation } from "@react-navigation/native";
import { RootState } from "../../redux/reducers";
import { setAllowServiceRequests } from "../../redux/actions/actionCreators";
import { setBannerMessagesSeen } from "../../redux/actions/actionCreators";
import { testProps } from "../../utils/testProps";
import { StorageKeys } from "../../enums/storage-keys";
import { UserService } from "../../api/UserService";
import * as storage from "../../utils/storage";
import {
  CognitoUser,
  CognitoUserPool,
  CognitoAccessToken,
  CognitoRefreshToken,
  CognitoUserSession,
  CognitoIdToken,
} from "amazon-cognito-identity-js";
import config from "../../../config";
import { DomusoIcon } from "../../../assets/domuso-icon";
import { theme as themeUiComponent, TextInput, Button } from "ui-components";
import { domusoResponsiveStyleHelper } from "../new/utils/domuso-responsive";
import { Grid, Container, Row, Column } from "ui-grid";
import { Footer } from "../../components/new/footer/footer";
import { redirectToAuthUI } from "../../utils/domusoAuthUI";
import { useDetermineChannel } from "../../hooks/useDetermineChannel";
import Auryc from "rn-auryc-sdk";

export const SignInScreen = ({ route }: any) => {
  const logger = useSelector((state: RootState) => state.logger);
  const theme = useTheme();
  const sessionExpired = useSelector((state: RootState) => state.auth.sessionExpired);
  const reservations = useSelector((state: RootState) => state.reservation.reservations);
  const navigation = useNavigation();
  const styles = getStyles(theme as ThemeType);
  const isMounted = useIsMounted();
  const dispatch = useDispatch();
  const [isLoading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [shouldRemember, setRemember] = useState(true);
  const [isPasswordHidden, setPasswordHidden] = useState(true);
  const { colors, iconSize } = theme;
  const inputAccessoryViewID = "inputAccessoryViewID1";
  const ref = [createRef(), createRef()];
  const [currentInputIndex, setCurrentInputIndex] = useState(0);
  const { params } = route;
  const [channel] = useDetermineChannel();

  if (!isMounted) {
    return null;
  }

  // reset reservations in redux
  // QUESTION: better way to do this?
  if (reservations && reservations.length) {
    dispatch(addReservations([]));
  }

  const {
    register,
    trigger,
    handleSubmit,
    setValue,
    getValues,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(loginSchema),
  });

  const handleSignIn = async () => {
    const { username, password } = getValues();
    try {
      setLoading(true);
      setErrorMsg("");
      const authData = await Auth.signIn({ username, password });
      const userService = new UserService(authData, dispatch);
      if (authData) {
        const attributes = authData?.attributes;
        userService.sendFootprints({
          username,
          interaction_type: "SIGN_IN",
          channel,
        });
        dispatch(
          addUser({
            cognitoUser: authData,
            userInfo: {
              username,
              domusoId: attributes["custom:domusoId"],
              given_name: attributes?.given_name,
              family_name: attributes?.family_name,
            },
            sessionExpired: false,
          })
        );
      }
    } catch (error) {
      //await logger.error(error);
      setErrorMsg(getCognitoErrorMessage(error));
      await logger.info("signing out from unauth");
      dispatch(signOutUser());
      setLoading(false);
    }
  };
  const handleFieldUpdate = async (field: string, val: string) => {
    val = val.trim();
    setValue(field, val);
    await trigger(field);
  };

  const authenticateWithTokensReactFix = async () => {
    // Hack for redirection from auth-ui to react web so a user does not need to authenticate
    // one more time.
    await authenticateWithTokens();
  };

  useEffect(() => {
    authenticateWithTokensReactFix();
  });

  useEffect(() => {
    logger.info("Rendering sign in screen");
    register(UserFields.USERNAME);
    register(UserFields.PASSWORD);
  }, [register]);

  const layoutEffect = useCallback(() => {
    (async () => {
      dispatch(setBannerMessagesSeen([]));
      dispatch(setAllowServiceRequests(false));
      await storage.save(StorageKeys.ALLOW_SERVICE_REQUESTS, false);
    })();
  }, []);
  useFocusEffect(layoutEffect);

  const shouldRememberStatus = shouldRemember ? "checked" : "unchecked";

  const renderErrorNotice = () => {
    if (!errorMsg && !sessionExpired) return null;
    return (
      <InfoBox
        headline={
          errorMsg
            ? (translate("authStack.loginError") as string)
            : (translate("authStack.sessionExpiredHeader") as string)
        }
        infoBoxType={InfoBoxType.ORANGE}
        headlineIconType={InfoBoxIconType.EXCLAMATION}>
        <Text>{errorMsg ? errorMsg : translate("authStack.sessionExpiredMessage")}</Text>
      </InfoBox>
    );
  };

  /**
   * This function is being used for web if redirection is taking place from auth-ui.
   */
  const authenticateWithTokens = async () => {
    if (Platform.OS == "web") {
      // Read local storage to get tokens in case the user is redirected from auth-ui;
      let authAccessToken = await storage.loadString(StorageKeys.WEB_APP_ACCESS_TOKEN_COOKIE);
      let authIdToken = await storage.loadString(StorageKeys.WEB_APP_ID_TOKEN_COOKIE);
      let authRefreshToken = await storage.loadString(StorageKeys.WEB_APP_REFRESH_TOKEN_COOKIE);
      if (authAccessToken && authIdToken && authRefreshToken) {
        const userPool = new CognitoUserPool({
          UserPoolId: config.userPoolId,
          ClientId: config.userPoolClientId,
        });
        const cognitoIdToken = new CognitoIdToken({
          IdToken: authIdToken,
        });
        const cognitoAccessToken = new CognitoAccessToken({
          AccessToken: authAccessToken,
        });
        const cognitoRefreshToken = new CognitoRefreshToken({
          RefreshToken: authRefreshToken,
        });
        const username = cognitoIdToken.payload["cognito:username"];
        const user = new CognitoUser({
          Username: username,
          Pool: userPool,
        });
        user.setSignInUserSession(
          new CognitoUserSession({
            AccessToken: cognitoAccessToken,
            IdToken: cognitoIdToken,
            RefreshToken: cognitoRefreshToken,
          })
        );
        const userService = new UserService(user, dispatch);

        userService.sendFootprints({
          username,
          interaction_type: "SIGN_IN",
          channel,
        });

        // Need to dispatch action to add user to skip the authentication.
        dispatch(
          addUser({
            cognitoUser: user,
            userInfo: {
              username,
              domusoId: cognitoIdToken?.payload["custom:domusoId"],
              given_name: cognitoIdToken?.payload?.given_name,
              family_name: cognitoIdToken.payload?.family_name,
            },
            sessionExpired: false,
          })
        );
      } else {
        redirectToAuthUI();
      }
    }
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === "ios" ? "padding" : "height"}
      keyboardVerticalOffset={
        Platform.OS === "web" ? undefined : Platform.OS == "android" ? 100 : 155
      }>
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollableArea}
        keyboardShouldPersistTaps={"handled"}>
        <StatusBar
          hidden={false}
          translucent={false}
          backgroundColor={themeUiComponent.colors.white}
          barStyle={"dark-content"}
        />
        <View style={styles.inner}>
          <Grid>
            <Container>
              <Row>
                <Column span={{ xs: false, sm: 2, md: 2.75, lg: 3.75 }} />
                <Column span={{ xs: 12, sm: 8, md: 6.5, lg: 4.5 }}>
                  <View style={{ marginVertical: 15 }}>{renderErrorNotice()}</View>
                  <Stack space="small">
                    <View style={{ alignItems: "center" }}>
                      <DomusoIcon height={45} width={45} color={themeUiComponent.colors.primary} />
                      <View style={{ marginTop: 10 }}>
                        <Text
                          style={styles.loginHeading}
                          text={translate("authStack.login2") as string}
                        />
                      </View>
                      <View style={{ marginVertical: 30 }}>
                        <Text
                          style={styles.subHeading}
                          text={translate("authStack.loginEnterEmail") as string}
                        />
                      </View>
                    </View>
                  </Stack>
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2.75, lg: 3.75 }} />
              </Row>
              <Row>
                <Column span={{ xs: false, sm: 2, md: 2.75, lg: 3.75 }} />
                <Column span={{ xs: 12, sm: 8, md: 6.5, lg: 4.5 }}>
                  <View>
                    <Stack space="smaller">
                      <TextInput
                        label={translate("authStack.emailOrUsername") as string}
                        placeholderStyle={{ flex: 0 }}
                        onChangeText={(val) => handleFieldUpdate(UserFields.USERNAME, val)}
                        returnKeyType="next"
                        inputAccessoryViewID={inputAccessoryViewID}
                        ref={ref[0] as any}
                        onFocus={() => setCurrentInputIndex(0)}
                        onSubmitEditing={() => (ref[1].current as any).focus()}
                        error={errors[UserFields.USERNAME]}
                        {...testProps(UserFields.USERNAME)}
                      />
                      <View ref={(x) => Auryc.markViewAsSensitiveInformation(x)} accessible={true}>
                        <TextInput
                          label={translate("authStack.password") as string}
                          placeholderStyle={{ flex: 0 }}
                          secureTextEntry={true}
                          returnKeyType="go"
                          inputAccessoryViewID={inputAccessoryViewID}
                          ref={ref[1] as any}
                          onFocus={() => setCurrentInputIndex(1)}
                          onSubmitEditing={handleSubmit(handleSignIn)}
                          onChangeText={(val) => handleFieldUpdate(UserFields.PASSWORD, val)}
                          rightIconColor={themeUiComponent.colors.primary}
                          error={errors[UserFields.PASSWORD]}
                          {...testProps(UserFields.PASSWORD)}
                        />
                      </View>
                    </Stack>
                    <View style={{ flexDirection: "row" }}>
                      <Text
                        style={[styles.link, styles.forgotPw]}
                        onPress={() => navigation.navigate("ForgotPassword")}
                        tx="authStack.forgotPassword"
                      />
                    </View>
                    <View style={{ marginTop: 40 }}>
                      <View>
                        <Button
                          text={translate("authStack.login") as string}
                          loading={isLoading}
                          onPress={handleSubmit(handleSignIn)}
                          textStyle={{ fontFamily: "Lato_700Bold" }}
                          {...testProps("login-button")}
                        />
                      </View>
                      <View style={{ marginVertical: 15 }}>
                        <Text style={styles.orText} text="OR" />
                      </View>
                      <View>
                        <Button
                          type="secondary"
                          text={translate("authStack.createAccount") as string}
                          onPress={() => navigation.navigate("SignUp")}
                          {...testProps("create-account-button")}
                        />
                      </View>
                    </View>
                  </View>
                  {Platform.OS === "ios" ? (
                    <InputAccessoryNavigation
                      inputAccessoryViewID={inputAccessoryViewID}
                      ref={ref as any}
                      currentInputIndex={currentInputIndex}
                    />
                  ) : null}
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2.75, lg: 3.75 }} />
              </Row>
            </Container>
          </Grid>
        </View>

        <Spacer size="large" />
      </ScrollView>
      <Footer />
    </KeyboardAvoidingView>
  );
};

const getCognitoErrorMessage = (err: NodeJS.ErrnoException): string => {
  const { NOT_AUTHORIZED, USER_NOT_FOUND, RESET_REQUIRED } = CognitoErrors;
  const { code } = err;
  const defaultMessage = translate("authStack.errors.default") || "";
  switch (code) {
    case NOT_AUTHORIZED:
      return defaultMessage.includes("Temporary password")
        ? defaultMessage
        : translate("authStack.errors.notAuthorized") || "";
    case USER_NOT_FOUND:
      return translate("authStack.errors.notFound") || "";
    case RESET_REQUIRED:
      return translate("authStack.errors.resetRequired") || "";
    default:
      return defaultMessage;
  }
};

const getStyles = (theme: ThemeType) => {
  const { colors, fontSize, spacing } = theme;
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: themeUiComponent.colors.white,
    },
    scrollView: { flexGrow: 0 },
    scrollableArea: {},
    inner: {
      ...domusoResponsiveStyleHelper({
        webPhone: {},
        webTablet: {},
        webDesktop: {},
        iosPhone: {},
        iosTablet: {},
        androidPhone: {},
        androidTablet: {},
      }),
    },
    text: {
      fontSize: fontSize.large,
      color: colors.text,
    },
    label: {
      fontSize: fontSize.large,
      marginLeft: spacing.small,
    },
    inlineContainer: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
    },
    link: {
      fontSize: fontSize.large,
      textDecorationLine: "underline",
    },
    forgotPw: {
      color: colors.text,
      fontSize: fontSize.large,
      marginTop: 5,
    },
    createAccountContainer: {
      marginLeft: "auto",
      marginRight: "auto",
    },
    noAccountText: {
      marginRight: spacing.tiny,
      color: colors.darkGrey,
    },
    loginHeading: {
      fontSize: fontSize.largerPlus,
      fontWeight: "bold",
    },
    subHeading: {
      fontSize: 18,
      textAlign: "center",
    },
    orText: {
      fontSize: 18,
      textAlign: "center",
      fontWeight: "bold",
    },
  });
};
